import React, { useState } from "react"
import { useMutation } from "urql"

import { Button } from "../../../components/shared/Buttons"
import { useToast } from "../../../contexts/ToastContext"

import { START_CALENDAR_CUTOVER_MUTATION } from "./graphql"
import MigrationBlockers from "./MigrationBlockers"
import { canStartCutover, migrationCounts, migrationStateLabel, MIGRATION_COUNT_KEYS } from "./selectors"
import { CalendarMigrationBlocker, CalendarMigrationStatus, StartCutoverPayload } from "./types"

interface Props {
  migration: CalendarMigrationStatus
  onChanged: () => void
}

const COUNT_LABELS: Record<(typeof MIGRATION_COUNT_KEYS)[number], string> = {
  pending: "Waiting",
  matched: "Matched",
  recreated: "Recreated",
  verified: "Verified",
  ambiguous: "Needs review",
  failed: "Failed"
}

// Migration progress for one legacy→native transfer. Shows counts and
// user-safe blocker copy only — never event titles or provider UIDs. The
// correlation id is a safe support reference.
const MigrationProgressPanel = ({ migration, onChanged }: Props) => {
  const [submitting, setSubmitting] = useState(false)
  const [freshBlockers, setFreshBlockers] = useState<CalendarMigrationBlocker[]>([])
  const [, startCutover] = useMutation<{ startCalendarCutover: StartCutoverPayload }>(START_CALENDAR_CUTOVER_MUTATION)
  const { showToast } = useToast()

  const counts = migrationCounts(migration.stats)
  const total = migration.stats?.total || 0
  const cutoverConnectionPublicId = migration.sourceConnectionPublicId

  const executeCutover = () => {
    if (!cutoverConnectionPublicId) return

    setSubmitting(true)
    startCutover({ connectionPublicId: cutoverConnectionPublicId, dryRun: false }).then((result) => {
      setSubmitting(false)
      const payload = result.data?.startCalendarCutover

      if (payload?.result === "success") {
        setFreshBlockers([])
        showToast("Your calendar has been switched to the new sync")
        onChanged()
      } else if (payload?.result === "blocked") {
        // The gate can re-block between render and execution — surface the
        // fresh blockers instead of pretending success.
        setFreshBlockers(payload.blockers || [])
        showToast({ content: "The switch is not ready yet. See what needs attention below.", type: "error" })
        onChanged()
      } else {
        showToast({ content: payload?.errors?.[0] || "Could not switch your calendar", type: "error" })
      }
    })
  }

  return (
    <div className="mt-3 rounded-lg border border-gray-light p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="font-bold">Calendar transfer: {migrationStateLabel(migration.state)}</div>
          {total > 0 && (
            <div className="text-sm text-gray-dark">
              {counts.verified} of {total} events verified
            </div>
          )}
        </div>
        {canStartCutover(migration) && cutoverConnectionPublicId && (
          <Button onClick={executeCutover} disabled={submitting} className="" floatingInMobile={false} style={{}}>
            {submitting ? "Switching…" : "Switch to new sync"}
          </Button>
        )}
      </div>

      <dl className="mt-3 grid grid-cols-3 gap-2 text-sm sm:grid-cols-2">
        {MIGRATION_COUNT_KEYS.map((key) => (
          <div key={key} className="rounded bg-gray-ultralight px-3 py-2">
            <dt className="text-gray-dark">{COUNT_LABELS[key]}</dt>
            <dd className="font-bold">{counts[key]}</dd>
          </div>
        ))}
      </dl>

      <MigrationBlockers blockers={freshBlockers.length > 0 ? freshBlockers : migration.blockers} />

      {migration.correlationId && (
        <p className="mt-3 text-xs text-gray-dark">
          Support reference: <span className="font-mono">{migration.correlationId}</span>
        </p>
      )}
    </div>
  )
}

export default MigrationProgressPanel
